import React, { useState } from "react";
import "./UploadTranscript.css";
import { apiPost } from "../../services/api";
import { SidePanel } from "../shared/SidePanel";
import { FormField, Button } from "../shared/FormField";
import { Session } from "../../types";

interface UploadTranscriptProps {
  isOpen: boolean;
  onClose: () => void;
  onUploaded: (session: Session) => void;
}

export function UploadTranscript({ isOpen, onClose, onUploaded }: UploadTranscriptProps) {
  const [title, setTitle] = useState("");
  const [transcript, setTranscript] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    setTranscript(text);
    if (!title) setTitle(file.name.replace(/\.[^.]+$/, ""));
  };

  const handleSubmit = async () => {
    if (!transcript.trim()) return;
    setUploading(true);
    setError("");

    try {
      const result = await apiPost<Session>("/api/sessions/upload-transcript", {
        transcript: transcript.trim(),
        title: title.trim() || "Uploaded Transcript",
      });
      onUploaded(result);
      setTitle("");
      setTranscript("");
      onClose();
    } catch (err) {
      console.error("Failed to upload transcript:", err);
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Meeting Transcript"
      width="600px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={uploading || !transcript.trim()}>
            {uploading ? "Scoring..." : "Upload & Score"}
          </Button>
        </>
      }
    >
      <p className="protrain-upload-hint">
        Upload a meeting transcript to get AI-powered scoring and feedback. The transcript will be analyzed against your configured KPIs.
      </p>

      {error && <div className="protrain-upload-error">{error}</div>}

      <FormField label="Title (optional)" htmlFor="transcript-title">
        <input
          id="transcript-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Discovery call with Acme Corp"
        />
      </FormField>

      <FormField label="Upload a file">
        <input
          type="file"
          accept=".txt,.csv,.md,.log"
          onChange={handleFileUpload}
          className="protrain-upload-file-input"
        />
      </FormField>

      <FormField label="Or paste transcript" htmlFor="transcript-text">
        <textarea
          id="transcript-text"
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder={"Rep: Hi, this is Alex from Nimbus. Do you have a moment?\nProspect: I'm pretty busy. What's this about?\nRep: I understand. I'll be brief..."}
          className="protrain-upload-textarea"
        />
      </FormField>

      <div className="protrain-upload-format-hint">
        <p>Supported formats:</p>
        <ul>
          <li><code>Rep: message</code> or <code>Prospect: message</code></li>
          <li><code>Seller: message</code> or <code>Customer: message</code></li>
          <li><code>Agent: message</code> or <code>Client: message</code></li>
        </ul>
      </div>
    </SidePanel>
  );
}
